import { createRouter, createWebHashHistory, createWebHistory } from "vue-router";
import { fetchMe } from '@/services/user';
import { tokenStore } from '@/services/tokenStore';

// Detection (vorm. SIEM)
import Tags from "../views/siem/viewTags.vue";
import Workflows from "../views/siem/viewWorkflows.vue";
import WorkflowDetails from "@/views/siem/viewWorkflowDetails.vue";

// Dashboard & Login
import DashMain from "@/views/dashboard/dashMain.vue";
import Login from "../views/viewLogin.vue";

// Threat Intelligence (vorm. HackWatch)
import ThreatActors from "@/views/hackwatch/viewGroups.vue";
import ThreatActorDetail from "@/views/hackwatch/viewGroupDetails.vue";
import RansomwarePosts from "@/views/hackwatch/viewHackPosts.vue";
import FileExtensions from "@/views/hackwatch/viewExtension.vue";

// User, Threat News
import viewCompanies from "@/views/admin/viewAdminCompanies.vue";
import NewsSources from "@/views/news/viewQuellen.vue";
import NewsFeed from "@/views/news/viewStart.vue";

import viewAdminUsers from "@/views/admin/viewAdminUsers.vue";

const APP_NAME = 'secDASH'
const DEFAULT_TITLE = 'secDASH'


const routes = [
  { path: "/", redirect: "/dashboard" },
  { path: "/login",
    name: "login",
    component: Login,
    meta: {
      perm: 'dashboard.access',
      title: 'Login'
    }
   },
  { path: "/auth/forgot-password",
    name: "forgotPassword",
    component: () => import('@/views/auth/viewForgotPassword.vue'),
    meta: { title: 'Passwort vergessen' }
  },
  { path: "/auth/reset-password",
    name: "resetPassword",
    component: () => import('@/views/auth/viewSetPassword.vue'),
    meta: { title: 'Passwort setzen' }
  },

  // ── Dashboard ──────────────────────────────────────────────────────────
  { path: "/dashboard", name: "dashboard", component: DashMain,
    meta: { perm: 'dashboard.access', title: 'Dashboard' }
  },
  { path: "/dashboard/activity", name: "dashboardActivity",
    component: () => import('@/views/dashboard/viewActivity.vue'),
    meta: { perm: 'dashboard.access', title: 'Aktivitäten' }
  },

  // ── Threat News (vorm. News + HackWatch Posts) ─────────────────────────
  { path: "/threat-news",
    name: "ThreatNewsOverview",
    component: () => import('@/views/threatnews/viewOverview.vue'),
    meta: { perm: 'news.access', title: 'Threat News' }
  },
  { path: "/threat-news/news",
    name: "ThreatNewsFeed",
    component: NewsFeed,
    meta: { perm: 'news.access', title: 'News Feed' }
  },
  {
    path: '/threat-news/news/:id',
    name: 'ThreatNewsItemDetail',
    component: () => import('@/views/news/viewNewsItemDetails.vue'),
    props: true,
    meta: { perm: 'news.access', title: 'News Detail' }
  },
  { path: "/threat-news/posts",
    name: "ThreatNewsPosts",
    component: RansomwarePosts,
    meta: { perm: 'hackwatch.access', title: 'Ransomware Posts' }
  },
  {
    path: '/threat-news/posts/:postId',
    name: 'ThreatNewsPostDetail',
    component: () => import('@/views/hackwatch/viewHackPostDetails.vue'),
    props: true,
    meta: { perm: 'hackwatch.access', title: 'Ransomware Post Detail' }
  },
  { path: "/threat-news/victims",
    name: "ThreatNewsVictims",
    component: () => import('@/views/hackwatch/viewVictims.vue'),
    meta: { perm: 'hackwatch.access', title: 'Betroffene Unternehmen' }
  },
  {
    path: '/threat-news/victims/:id',
    name: 'ThreatNewsVictimDetail',
    component: () => import('@/views/hackwatch/viewVictimDetail.vue'),
    props: true,
    meta: { perm: 'hackwatch.access', title: 'Betroffenes Unternehmen' }
  },
  { path: "/threat-news/sources",
    name: "ThreatNewsSources",
    component: NewsSources,
    meta: { perm: 'news.access', title: 'News Quellen' }
  },
  { path: "/threat-news/map",
    name: "ThreatNewsMap",
    component: () => import('@/views/threatnews/viewMap.vue'),
    meta: { perm: 'hackwatch.access', title: 'Lagekarte' }
  },

  // ── Threat Intelligence (vorm. HackWatch) ──────────────────────────────
  { path: "/threat-intel",
    name: "ThreatIntelOverview",
    component: () => import('@/views/threatintel/viewOverview.vue'),
    meta: { perm: 'hackwatch.access', title: 'Threat Intelligence' }
  },
  {
    path: '/threat-intel/mitre',
    name: 'ThreatIntelMitre',
    component: () => import('@/views/hackwatch/viewTTPs.vue'),
    meta: { perm: 'hackwatch.access', title: 'MITRE ATT&CK' }
  },
  {
    path: '/threat-intel/mitre/:id',
    name: 'ThreatIntelMitreDetail',
    component: () => import('@/views/hackwatch/viewTtpDetail.vue'),
    props: true,
    meta: { perm: 'hackwatch.access', title: 'MITRE ATT&CK Technique' }
  },
  { path: "/threat-intel/actors",
    name: "ThreatIntelActors",
    component: ThreatActors,
    meta: { perm: 'hackwatch.access', title: 'Threat Actors' }
  },
  {
    path: "/threat-intel/actors/:id",
    name: "ThreatIntelActorDetail",
    component: ThreatActorDetail,
    props: (route) => ({ groupId: Number(route.params.id) }),
    meta: { perm: 'hackwatch.access', title: 'Threat Actor' }
  },
  {
    path: '/threat-intel/tools',
    name: 'ThreatIntelTools',
    component: () => import('@/views/hackwatch/viewTools.vue'),
    meta: { perm: 'hackwatch.access', title: 'Attacker Tools' }
  },
  {
    path: '/threat-intel/iocs',
    name: 'ThreatIntelIOCs',
    component: () => import('@/views/hackwatch/viewIOCs.vue'),
    meta: { perm: 'hackwatch.access', title: 'IOCs' }
  },
  { path: "/threat-intel/extensions",
    name: "ThreatIntelExtensions",
    component: FileExtensions,
    meta: { perm: 'hackwatch.access', title: 'File Extensions' }
  },

  // ── Detection (vorm. SIEM) ─────────────────────────────────────────────
  { path: "/detection",
    name: "DetectionOverview",
    component: () => import('@/views/detection/viewOverview.vue'),
    meta: { perm: 'siem_workflow.access', title: 'Detection' }
  },
  { path: "/detection/workflows", name: "DetectionWorkflows", component: Workflows,
    meta: { perm: 'siem_workflow.access', title: 'Detection Workflows' }
  },
  {
    path: "/detection/workflows/:id",
    name: "DetectionWorkflowDetail",
    component: WorkflowDetails,
    props: (route) => ({ workflowId: Number(route.params.id) }),
    meta: { perm: 'siem_workflow.access', title: 'Workflow' }
  },
  { path: "/detection/streams", name: "DetectionStreams",
    component: () => import('@/views/siem/viewSiemStreams.vue'),
    meta: { perm: 'siem_workflow.access', title: 'Detection Streams' }
  },
  { path: "/detection/tags", name: "DetectionTags", component: Tags,
    meta: { perm: 'siem_tags.access', title: 'Detection Tags' }
  },

  // ── Partner Customers ──────────────────────────────────────────────────
  {
    path: '/partner/customers',
    name: 'PartnerCustomersOverview',
    component: () => import('@/views/partner/viewPartnerCustomersOverview.vue'),
    meta: { perm: 'partner_customers.view', requiresPartner: true, title: 'Customers' },
  },
  {
    path: '/partner/customers/list',
    name: 'PartnerCustomers',
    component: () => import('@/views/partner/viewPartnerCustomers.vue'),
    meta: { perm: 'partner_customers.view', requiresPartner: true, title: 'Customer List' },
  },
  {
    path: '/partner/customers/:id',
    name: 'PartnerCustomerDetail',
    component: () => import('@/views/partner/viewPartnerCustomerDetail.vue'),
    meta: { perm: 'partner_customers.view', requiresPartner: true, title: 'Customer Detail' },
  },

  // ── User & Admin ───────────────────────────────────────────────────────
  // Settings: eigene Plattform-Sektion (Sub-Panel in layoutShell, siehe
  // navSections 'settings'). Flache Routen — jede View wrappt layoutShell selbst.
  { path: "/user/settings", redirect: "/user/settings/general" },
  { path: "/user/settings/general",
    name: "SettingsGeneral",
    component: () => import('@/views/users/viewSettingsGeneral.vue'),
    meta: { perm: 'news.access', title: 'Settings' } },
  { path: "/user/settings/security",
    name: "SettingsSecurity",
    component: () => import('@/views/users/viewSettingsSecurity.vue'),
    meta: { perm: 'news.access', title: 'Settings' } },
  { path: "/user/settings/notifications",
    name: "SettingsNotifications",
    component: () => import('@/views/users/viewSettingsNotifications.vue'),
    meta: { perm: 'news.access', title: 'Settings' } },
  { path: "/user/settings/api-keys",
    name: "SettingsApiKeys",
    component: () => import('@/views/users/viewSettingsApiKeys.vue'),
    meta: { perm: 'news.access', title: 'Settings' } },

  { path: "/admin",
    name: "AdminOverview",
    component: () => import('@/views/admin/viewOverview.vue'),
    meta: { perm: 'admin.access', requiresAdmin: true, title: 'Administration' }
  },
  { path: "/admin/users",
    name: "Admin Users",
    component: viewAdminUsers,
    // Company-Admins (company_users.view) dürfen ohne globale Admin-Rechte rein;
    // das mandanten-begrenzte Scoping erzwingt die API.
    meta: { requiresAdmin: true, allowPerms: ['company_users.view'], title: 'Admin Users' }
  },
  { path: "/admin/companies",
    name: "Admin Companies",
    component: viewCompanies,
    meta: { perm: 'admin.access', requiresAdmin: true, title: 'Admin Companies' }
  },
  {
    path: '/admin/roles',
    name: 'AdminRoles',
    component: () => import('@/views/admin/viewAdminRoles.vue'),
    meta: { perm: 'admin.access', requiresAdmin: true, title: 'Admin Roles' }
  },
  {
    path: '/admin/api-keys',
    name: 'AdminAPIkeys',
    component: () => import('@/views/admin/viewAdminAPIkeys.vue'),
    meta: { perm: 'admin.access', requiresAdmin: true, title: 'Admin API Keys' }
  },
  {
    path: '/admin/audit-logs',
    name: 'AdminAuditLogs',
    component: () => import('@/views/admin/viewAdminAuditLogs.vue'),
    meta: { perm: 'admin.access', requiresAdmin: true, title: 'Audit Logs' }
  },
  {
    path: '/admin/status',
    name: 'AdminStatus',
    component: () => import('@/views/admin/viewStatus.vue'),
    meta: { perm: 'admin.access', requiresAdmin: true, title: 'System Status' }
  },

];

const router = createRouter({
  // History-Modus abhängig vom Build-Ziel (gleicher Schalter wie publicPath):
  // - Capacitor/iOS-WebView (VUE_APP_CAPACITOR=1): createWebHashHistory() ist Pflicht
  //   — capacitor://localhost/#/route, ohne Server-seitiges URL-Rewriting würde
  //   createWebHistory im WebView zu 404 führen.
  // - Web/Docker (Default): createWebHistory() — saubere Pfad-URLs ohne '#'.
  //   nginx liefert für alle Pfade index.html (try_files … /index.html), daher
  //   funktionieren Deep-Links wie /auth/reset-password?token=… direkt. Das ist
  //   robuster als '#'-Fragmente, die E-Mail-Clients/Security-Gateways strippen können.
  history: process.env.VUE_APP_CAPACITOR === '1'
    ? createWebHashHistory()
    : createWebHistory(process.env.BASE_URL),
  routes,
});

// Basis-Auth-Guard: öffentliche Seiten durchlassen, sonst Token prüfen.
// tokenStore.getSync() ist nach tokenStore.init() (in main.js) synchron verfügbar.
router.beforeEach((to, from, next) => {
  const publicPages = ["/login", "/auth/forgot-password", "/auth/reset-password"];
  if (publicPages.includes(to.path)) return next();

  if (tokenStore.getSync()) return next();

  return next({ path: "/login", query: { redirect: to.fullPath } });
});




// Einfacher Admin-Guard
router.beforeEach(async (to, from, next) => {
  if (!to.meta.requiresAdmin) return next();

  if (!tokenStore.getSync()) {
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  try {
    const me = await fetchMe();
    const perms = me?.permissions || [];
    const isAdmin = me?.is_admin === true || me?.isAdmin === true
      || me?.role === 'Admin' || perms.includes('admin.access');
    if (isAdmin) return next();
    // allowPerms: erlaubt Nicht-Admins mit passender Permission (z.B. Company-Admin)
    const allow = to.meta.allowPerms || [];
    if (allow.some(p => perms.includes(p))) return next();
    return next({ path: '/403' });
  } catch (e) {
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }
});


// Partner guard — requires company.is_partner = true + partner_customers.view permission
router.beforeEach(async (to, from, next) => {
  if (!to.meta.requiresPartner) return next();

  if (!tokenStore.getSync()) {
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  try {
    const me = await fetchMe();
    const isPartner  = me?.company_is_partner === true;
    const hasPerm    = me?.permissions?.includes('partner_customers.view') ||
                       me?.is_admin === true || me?.role === 'Admin';
    if (isPartner && hasPerm) return next();
    return next({ path: '/403' });
  } catch (e) {
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }
});

router.afterEach((to) => {
  const match = [...to.matched].reverse().find(r => r.meta && r.meta.title)
  let title = null
  if (match) {
    title = typeof match.meta.title === 'function' ? match.meta.title(to) : match.meta.title
  }
  document.title = title ? `${title} · ${APP_NAME}` : DEFAULT_TITLE
})

export default router;
